import { useEffect, useMemo } from 'react';

import useEventCallback from './useEventCallback';

type EventMaps =
  | keyof WindowEventMap
  | keyof HTMLElementEventMap
  | keyof DocumentEventMap;

interface ListenerType {
  addEventListener(
    name: string,
    handler: (event?: any) => void,
    ...args: any[]
  ): unknown;

  removeEventListener(
    name: string,
    handler: (event?: any) => void,
    ...args: any[]
  ): unknown;
}

type EventTargetType<K extends EventMaps> = K extends keyof WindowEventMap
  ? Window
  : K extends keyof DocumentEventMap
  ? K extends keyof HTMLElementEventMap
    ? HTMLElement | Document
    : Document
  : K extends keyof HTMLElementEventMap
  ? HTMLElement
  : never;

type EventCallbackType<K extends EventMaps> = K extends keyof WindowEventMap
  ? (this: Window, ev: WindowEventMap[K]) => any
  : K extends keyof DocumentEventMap
  ? (this: Document, ev: DocumentEventMap[K]) => any
  : K extends keyof HTMLElementEventMap
  ? (this: HTMLElement, ev: HTMLElementEventMap[K]) => any
  : never;

const isListenerType1 = (target: any): target is ListenerType => {
  return !!target.addEventListener;
};

/**
 * Registers an event listener on the window which automatically cancels
 * itself when the component unmounts.
 *
 * In addition, it will transform the passed callback and options into
 * stable representations which will not cause the event to re-register
 * needlessly.
 *
 * @remarks
 *  It is possible that `signal` could cause issues if it can not be
 *  stringified.
 *
 * @param options Note that passing an object will only cause re-render if
 * the stringified object changes.
 */
function useEventListener<
  K extends
    | keyof WindowEventMap
    | keyof DocumentEventMap
    | keyof HTMLElementEventMap,
  C extends EventCallbackType<K>,
  T extends EventTargetType<K>,
>(
  eventName: K,
  target: T | undefined,
  onEvent?: C,
  options?: undefined | boolean | AddEventListenerOptions
) {
  const isEventActive = !!onEvent && !!target;
  const stringifiedOptions = useMemo(() => {
    try {
      return JSON.stringify(options);
    } catch {
      return options;
    }
  }, [options]);

  const stableListener = useEventCallback(
    <E extends Parameters<C>[0]>(e: E) => {
      if (isEventActive) {
        (onEvent as unknown as (this: T, e: E) => void).call(target, e);
      }
    }
  );

  useEffect(() => {
    const listener: Parameters<T['addEventListener']>[1] = e => {
      stableListener(e);
    };

    if (isEventActive && isListenerType1(target)) {
      // console.log('register: ', eventName, target, options);
      target.addEventListener(eventName, listener, options);
    } else if (isEventActive) {
      // should never happen
      console.warn(
        'unsupported event target type ',
        eventName,
        target,
        options
      );
    }

    return () => {
      if (isEventActive && isListenerType1(target)) {
        // console.log('unregister: ', eventName, target);
        target.removeEventListener(eventName, listener, options);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eventName,
    stableListener,
    stringifiedOptions,
    target,
    isEventActive,
    // options, we do use stringifiedOptions instead
  ]);
}

export function useWindowEventListener<
  K extends keyof WindowEventMap,
  C extends EventCallbackType<K>,
>(eventName: K, onEvent?: C) {
  useEventListener(eventName as keyof WindowEventMap, window, onEvent);
}

// export function useDocumentEventListener<
//   K extends keyof DocumentEventMap,
//   C extends EventCallbackType<K>
// >(eventName: K, onEvent?: C) {
//   useEventListener(eventName as keyof DocumentEventMap, document, onEvent);
// }

/**
 * Registers a typed event listener on the given HTMLElement.  The element can be undefined or
 * null while waiting for the ref to populate.  However, the ref should be set using useState
 * to make sure the listener is updated when the ref becomes available.
 *
 * @param eventName The event name for the given HTMLElement
 * @param [element]
 * @param [onEvent]
 *
 * @example
 *  const [element, setElement] = useState<HTMLElement | null>(null);
 *  useElementEventListener('click', element, (evt) => {
 *    console.log('clicked', evt);
 *  });
 *  return <div ref={setElement}>Click me</div>;
 */
// export function useElementEventListener<
//   K extends keyof HTMLElementEventMap,
//   C extends EventCallbackType<K>
// >(eventName: K, element?: HTMLElement | null, onEvent?: C) {
//   useEventListener(
//     eventName as keyof HTMLElementEventMap,
//     element ?? undefined,
//     onEvent
//   );
// }
